import { useMemo } from "react";
import type { CommissionEditRequestStatusFilter } from "@/hooks/queries/useCommissionEditRequests";
import { useCommissionEditRequests } from "@/hooks/queries/useCommissionEditRequests";
import { useCommissionAll } from "@/hooks/queries/useCommissionAll";
import { useCommissionTypes } from "@/hooks/queries/useCommissionTypes";
import { useUsers } from "@/hooks/queries/useUsers";
import {
  buildRequestContexts,
  buildTransactionMap,
  buildUserMap,
  filterRequestContexts,
  getRequestStats,
} from "../commissionEditQueue-calculations";

export function useCommissionEditQueueData(filters: {
  search: string;
  statusFilter: CommissionEditRequestStatusFilter;
  typeFilter: string;
}) {
  const { data: requests = [], isLoading: loadingRequests } = useCommissionEditRequests("all");
  const { data: transactionList = [] } = useCommissionAll();
  const { data: userList } = useUsers({});
  const { data: types = [] } = useCommissionTypes();

  const transactions = useMemo(() => buildTransactionMap(transactionList), [transactionList]);
  const users = useMemo(() => buildUserMap(userList?.data ?? []), [userList?.data]);
  const contexts = useMemo(
    () => buildRequestContexts({ requests, transactions, users, types }),
    [requests, transactions, types, users],
  );
  const filteredContexts = useMemo(
    () =>
      filterRequestContexts({
        contexts,
        search: filters.search,
        statusFilter: filters.statusFilter,
        typeFilter: filters.typeFilter,
      }),
    [contexts, filters.search, filters.statusFilter, filters.typeFilter],
  );
  const stats = useMemo(() => getRequestStats(requests), [requests]);

  return {
    requests,
    contexts,
    filteredContexts,
    stats,
    types,
    loadingRequests,
  };
}
